import type { AccountRiskProfile, AssessedRiskRating } from '@gcv/shared';

import { RISK_RATING_SOURCE_LABELS } from 'ui/apps/fi/shared/types';
import { isRated } from './is-rated';

export interface RiskRatingsTableRow {
  id: string;
  /** 0 pins the parent first; children follow in the order the API returned them. */
  order: number;
  isParent: boolean;
  name: string;
  licenseType: string;
  score?: number;
  rating?: AssessedRiskRating;
  source: string;
  lastAssessedOn?: string;
  /** The user id that set the rating; resolve to a name before showing it. Parent row only. */
  setByUserId?: string;
  overridden: boolean;
  /** Parent row only. */
  justification?: string;
  resultId?: string;
  isAssessed: boolean;
}

/** The parent row plus one per child. An unrated child is kept and marked unassessed. */
export const buildRiskRatingRows = (
  profile: AccountRiskProfile | undefined,
  parentName: string
): RiskRatingsTableRow[] => {
  if (!profile) {
    return [];
  }

  const { current_rating: current } = profile;

  const parentRow: RiskRatingsTableRow = {
    id: profile.dispensary_id,
    order: 0,
    isParent: true,
    name: parentName,
    licenseType: '--',
    score: current.score,
    rating: current.rating,
    source: current.source ? RISK_RATING_SOURCE_LABELS[current.source] : '--',
    lastAssessedOn: current.set_on,
    setByUserId: current.set_by,
    overridden: current.overridden,
    justification: current.justification,
    resultId: current.result_id,
    isAssessed: isRated(current.rating)
  };

  const childRows = (profile.children ?? []).map((child, index) => ({
    id: child.dispensary_id,
    order: index + 1,
    isParent: false,
    name: child.name,
    licenseType: child.license_type ?? '--',
    score: child.score,
    rating: child.rating,
    source: child.source ? RISK_RATING_SOURCE_LABELS[child.source] : '--',
    lastAssessedOn: child.last_assessed_on,
    overridden: false,
    resultId: child.result_id,
    isAssessed: isRated(child.rating)
  }));

  return [parentRow, ...childRows];
};
